import { EditOutlined, InboxOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Drawer } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form, type FormRule } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { useNotification } from "components/Notification/Notification"
import { Select } from "components/Select/Select"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Upload, type UploadFile } from "components/Upload/Upload"
import { useDisclosure } from "hooks/useDisclosure"
import { AdminVisitUpdatePayload, Visit } from "interface/visit"
import { useCallback, useMemo } from "react"
import { useAdminVisitUpdate } from "services/visits"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"
import { asDate, Dayjs } from "utils/date"
import { CONSTRAINTS } from "utils/file"
import { visitStatusOptions } from "utils/visits"
import { v4 as uuidv4 } from "uuid"
import { SelectTeams } from "./SelectTeams"

export function UpdateVisit({ visit }: { visit: Visit }) {
	const disclose = useDisclosure()
	const title = `Edit visit for ${visit.patient?.fullName ?? "unknown patient"}`

	return (
		<>
			<Tooltip.Dark title={title}>
				<Button
					type="text"
					size="large"
					icon={<EditOutlined aria-hidden />}
					aria-label={title}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<UpdateVisitForm title={title} visit={visit} onClose={disclose.close} />
			)}
		</>
	)
}

type UpdateVisitFormValues = {
	status: Visit["status"]
	fieldSupportTeamId: string
	notes?: string
	visitDate?: Dayjs
	completedDate?: Dayjs
	cancelDate?: Dayjs
	installationPhoto?: UploadFile[]
}

function UpdateVisitForm({
	visit,
	title,
	onClose,
}: {
	visit: Visit
	title: string
	onClose: VoidFunction
}) {
	const { notify } = useNotification()
	const [form] = Form.useForm<UpdateVisitFormValues>()
	const update = useAdminVisitUpdate(visit.id)
	const initialValues = useMemo<Partial<UpdateVisitFormValues>>(
		() => ({
			status: visit.status,
			fieldSupportTeamId: visit.fieldSupportTeam?.id,
			notes: visit.notes,
			visitDate: visit.visitDate ? asDate(visit.visitDate) : undefined,
			completedDate: visit.completedDate
				? asDate(visit.completedDate)
				: undefined,
			cancelDate: visit.cancelDate ? asDate(visit.cancelDate) : undefined,
			installationPhoto: [
				{
					uid: uuidv4(),
					name: `Current installation photo for ${visit.patient?.fullName ?? "visit"}`,
					status: "done",
					url: visit.installationPhotoUrl,
				},
			],
		}),
		[visit],
	)
	const status = Form.useWatch("status", form) ?? visit.status
	// COMPLETED is terminal, so the Select is disabled and `status` stays COMPLETED.
	const isCompleted = visit.status === "COMPLETED"
	const isCompleting = status === "COMPLETED" && !isCompleted
	const isUnscheduling =
		status === "UNSCHEDULED" && visit.status !== "UNSCHEDULED"
	const statusHint = isCompleted
		? "A completed visit keeps its status. Its dates can still be corrected."
		: isUnscheduling
			? "Unscheduling clears the visit date, its duration and any assigned agents."
			: undefined
	const handleFinish = useCallback(
		(values: UpdateVisitFormValues) => {
			update.mutate(buildPayload(values), {
				onSuccess: () => {
					notify.success({
						message: title,
						description: `The visit is now ${values.status.toLowerCase()}`,
					})
					onClose()
				},
				onError: (error) =>
					notify.error({
						message: title,
						description: error.message ?? GENERIC_ERROR_MESSAGE,
					}),
			})
		},
		[notify, onClose, title, update],
	)

	return (
		<Drawer
			open
			aria-label={title}
			title={<BackButton onClick={onClose}>Visits</BackButton>}
			onClose={onClose}
		>
			<h2 className="font-headings text-3xl font-semibold">{title}</h2>

			<Form<UpdateVisitFormValues>
				form={form}
				autoComplete="off"
				name={`admin-visit-update-${visit.id}`}
				layout="vertical"
				aria-label={title}
				initialValues={initialValues}
				className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
				onFinish={handleFinish}
			>
				<Form.Item
					name="status"
					label="Status"
					extra={statusHint}
					rules={baseRules}
				>
					<Select<Visit["status"]>
						virtual={false}
						aria-label="Status"
						disabled={isCompleted}
						options={visitStatusOptions(visit.allowedStatuses)}
					/>
				</Form.Item>

				<Form.Item
					name="fieldSupportTeamId"
					label="Field support team"
					rules={[{ required: true, message: "Please pick a team" }]}
				>
					<SelectTeams
						aria-label="Field support team"
						placeholder="Select a team"
					/>
				</Form.Item>

				{(status === "SCHEDULED" || status === "COMPLETED") && (
					<Form.Item
						name="visitDate"
						label="Visit date"
						extra={
							isCompleting
								? "The scheduled date is kept when a visit is completed."
								: undefined
						}
						rules={status === "SCHEDULED" ? dateRules : undefined}
					>
						<DatePicker.DateTime
							className="w-full"
							aria-label="Visit date"
							disabled={isCompleting}
						/>
					</Form.Item>
				)}

				{status === "COMPLETED" && (
					<Form.Item
						name="completedDate"
						label="Completed date"
						dependencies={["visitDate"]}
						rules={[...dateRules, notBeforeVisitDate]}
					>
						<DatePicker.DateTime
							className="w-full"
							aria-label="Completed date"
						/>
					</Form.Item>
				)}

				{status === "CANCELLED" && (
					<Form.Item
						name="cancelDate"
						label="Cancel date"
						rules={[{ required: true, message: "Please pick a cancel date" }]}
					>
						<DatePicker.DateTime className="w-full" aria-label="Cancel date" />
					</Form.Item>
				)}

				<Form.Item
					label="Installation photo"
					extra={
						visit.installationPhotoUrl
							? "Uploading a new photo replaces the current one."
							: undefined
					}
				>
					<Form.Item
						noStyle
						name="installationPhoto"
						valuePropName="fileList"
						getValueFromEvent={getFileList}
					>
						<Upload.Dragger
							maxCount={1}
							listType="picture"
							data-testid="Installation photo"
							accept={CONSTRAINTS.ALLOWED_MIME_TYPES.join(",")}
							beforeUpload={(file) => {
								if (file.size > CONSTRAINTS.IMAGE_MAX_FILE_SIZE) {
									void form.setFields([
										{
											name: "installationPhoto",
											value: [],
											errors: [CONSTRAINTS.IMAGE_MAX_SIZE_ERROR_MESSAGE],
										},
									])
									return Upload.LIST_IGNORE
								}
								return false
							}}
						>
							<InboxOutlined aria-hidden className="text-4xl text-green-60" />
							Click or drag an image here to upload
						</Upload.Dragger>
					</Form.Item>
				</Form.Item>

				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={4} aria-label="Notes" />
				</Form.Item>

				<Form.Item className="flex flex-col gap-2 pt-2">
					<Button
						type="primary"
						htmlType="button"
						loading={update.isPending}
						className="w-full"
						onClick={form.submit}
					>
						Save
					</Button>
					<ErrorStatus show={update.isError}>
						{update.error?.message ?? GENERIC_ERROR_MESSAGE}
					</ErrorStatus>
				</Form.Item>
			</Form>
		</Drawer>
	)
}

const buildPayload = (
	values: UpdateVisitFormValues,
): AdminVisitUpdatePayload => {
	const payload = {
		status: values.status,
		fieldSupportTeamId: values.fieldSupportTeamId,
		notes: values.notes ?? "",
		installationPhotoFile: values.installationPhoto?.[0]?.originFileObj,
	}

	switch (values.status) {
		case "SCHEDULED":
			return { ...payload, visitDate: values.visitDate?.toISOString() }
		case "COMPLETED":
			return {
				...payload,
				visitDate: values.visitDate?.toISOString(),
				completedDate: values.completedDate?.toISOString(),
			}
		case "CANCELLED":
			return { ...payload, cancelDate: values.cancelDate?.toISOString() }
		case "UNSCHEDULED":
			return payload
	}
}

const getFileList = ({ fileList }: { fileList: UploadFile[] }) => fileList

/** Mirrors the backend's `VisitCompletedDateBeforeVisitDate`, day granularity. */
const notBeforeVisitDate: FormRule = ({ getFieldValue }) => ({
	validator(_, value: Dayjs | undefined) {
		const visitDate = getFieldValue("visitDate") as Dayjs | undefined

		if (value && visitDate && value.isBefore(visitDate, "day")) {
			return Promise.reject(
				new Error("Completion date cannot be earlier than visit date"),
			)
		}
		return Promise.resolve()
	},
})

const baseRules: FormRule[] = [{ required: true }]
const dateRules: FormRule[] = [
	{ required: true, message: "Please pick a date" },
]
